import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { 
    Plus, 
    Users, 
    Calendar, 
    FolderKanban, 
    CheckSquare, 
    Clock, 
    Play, 
    Square, 
    Megaphone,
    Video,
    Award
} from "lucide-react";
import api from "../lib/api";
import { GlassCard, PageHeader, Skeleton } from "../components/shared";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

function fmtMin(min) {
    if (!min) return "0h 0m";
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h ${m}m`;
}

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [dist, setDist] = useState([]);
    const [pendingLeaves, setPendingLeaves] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [recentHires, setRecentHires] = useState([]);
    const [loading, setLoading] = useState(true);

    // Employee specific states
    const [today, setToday] = useState(null);
    const [history, setHistory] = useState([]);
    const [leaveStats, setLeaveStats] = useState(null);
    const [acting, setActing] = useState(false);

    const isEmployeeOrIntern = user?.role === "employee" || user?.role === "intern";

    useEffect(() => {
        if (!user) return;

        if (isEmployeeOrIntern) {
            Promise.all([
                api.get("/attendance/today").then((r) => setToday(r.data)),
                api.get("/attendance/me?days=30").then((r) => setHistory(r.data.items || [])),
                api.get("/leaves/me").then((r) => setLeaveStats(r.data)),
                api.get("/meetings").then((r) => {
                    setMeetings(r.data.items || []);
                })
            ]).catch(() => {})
              .finally(() => setLoading(false));
        } else {
            Promise.all([
                api.get("/dashboard/stats").then((r) => setStats(r.data)),
                api.get("/dashboard/department-distribution").then((r) => setDist(r.data.items || [])),
                api.get("/leaves/all").then((r) => {
                    const items = r.data.items || [];
                    setPendingLeaves(items.filter(item => item.status === "pending"));
                }),
                api.get("/meetings").then((r) => {
                    setMeetings(r.data.items || []);
                }),
                api.get("/employees?limit=3").then((r) => {
                    setRecentHires(r.data.items || []);
                })
            ]).catch(() => {})
              .finally(() => setLoading(false));
        }
    }, [user, isEmployeeOrIntern]);

    const punch = async (which) => {
        setActing(true);
        try {
            const coords = await new Promise((res) =>
                navigator.geolocation
                    ? navigator.geolocation.getCurrentPosition(
                          (p) => res({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
                          () => res({ latitude: null, longitude: null }),
                          { timeout: 3000 },
                      )
                    : res({ latitude: null, longitude: null }),
            );
            await api.post(`/attendance/punch-${which}`, coords);
            toast.success(`Punched ${which} successfully!`);
            
            // Reload local states
            const t = await api.get("/attendance/today");
            setToday(t.data);
            const h = await api.get("/attendance/me?days=30");
            setHistory(h.data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || `Punch ${which} failed`);
        } finally {
            setActing(false);
        }
    };

    // Department progress calculations (with screenshot fallbacks if empty)
    const getDeptDistribution = () => {
        if (dist.length > 0) {
            return [...dist].sort((a, b) => b.count - a.count).slice(0, 3);
        }
        return [
            { department: "Engineering", count: 14 },
            { department: "Founder Office", count: 1 },
            { department: "App Development", count: 1 }
        ];
    };

    const deptDist = getDeptDistribution();
    const maxDeptCount = Math.max(...deptDist.map(d => d.count), 1);

    // Recent hires list (with screenshot fallbacks if empty)
    const getRecentHiresList = () => {
        if (recentHires.length > 0) {
            return recentHires.slice(0, 3).map(emp => ({
                name: emp.name,
                role: emp.designation || "Team Member",
                dept: emp.department || "Engineering",
                date: emp.joining_date ? new Date(emp.joining_date).toLocaleDateString("en-US", { day: 'numeric', month: 'short', year: 'numeric' }) : "Recently",
                avatar: emp.avatar
            }));
        }
        return [
            { name: "Amrita Jadhav", role: "security analyst intern", dept: "Engineering", date: "19 Jun 2026" },
            { name: "Aastha Purohit", role: "SDE Intern", dept: "Engineering", date: "29 May 2026" },
            { name: "Tanya Tripathi", role: "junior backend developer intern", dept: "Engineering", date: "28 May 2026" }
        ];
    };

    const hiresList = getRecentHiresList();

    // Calculations for employee personal metrics
    const getPresentDays = () => history.filter(h => h.punch_in).length;
    const getLateDays = () => history.filter(h => h.is_late).length;

    if (loading) {
        return (
            <div className="space-y-8">
                <Skeleton className="h-10 w-48" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Skeleton className="h-36" />
                    <Skeleton className="h-36" />
                    <Skeleton className="h-36" />
                    <Skeleton className="h-36" />
                </div>
            </div>
        );
    }

    if (isEmployeeOrIntern) {
        const punchedIn = today?.punch_in;
        const punchedOut = today?.punch_out;

        return (
            <div className="space-y-8" data-testid="dashboard-view">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold font-heading text-slate-800 dark:text-slate-100">
                        Welcome back, {user?.name?.split(" ")[0] || "Employee"}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Have a productive day ahead! Keep track of your shifts and tasks.
                    </p>
                </div>

                {/* Personal Metric Cards Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Days Present */}
                    <GlassCard className="p-5 flex flex-col justify-between h-36">
                        <div>
                            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Present (This Month)</span>
                            <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                                {getPresentDays()} days
                            </div>
                        </div>
                        <span className="text-xs font-semibold text-emerald-500">
                            Active participation
                        </span>
                    </GlassCard>

                    {/* Late Punches */}
                    <GlassCard className="p-5 flex flex-col justify-between h-36">
                        <div>
                            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Late Punches</span>
                            <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                                {getLateDays()}
                            </div>
                        </div>
                        <span className="text-xs font-semibold text-amber-500">
                            Keep it up! Try to be on time
                        </span>
                    </GlassCard>

                    {/* Remaining Leaves */}
                    <GlassCard className="p-5 flex flex-col justify-between h-36">
                        <div>
                            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Leaves Balance</span>
                            <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                                {leaveStats ? (leaveStats.balance.paid + leaveStats.balance.sick + leaveStats.balance.casual) - (leaveStats.used.paid + leaveStats.used.sick + leaveStats.used.casual) : 36}
                            </div>
                        </div>
                        <span className="text-xs font-semibold text-slate-400">
                            Days available
                        </span>
                    </GlassCard>

                    {/* Upcoming Meetings */}
                    <GlassCard className="p-5 flex flex-col justify-between h-36">
                        <div>
                            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Meetings</span>
                            <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                                {meetings.length}
                            </div>
                        </div>
                        <span className="text-xs font-semibold text-primary">
                            Scheduled calls
                        </span>
                    </GlassCard>
                </div>

                {/* Middle Row: Punch Widget & Personal Attendance History */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Punch Widget */}
                    <GlassCard className="p-6 relative overflow-hidden flex flex-col justify-between min-h-[300px]">
                        <div className="absolute -right-20 -bottom-20 w-60 h-60 rounded-full bg-primary/15 blur-3xl" />
                        <div className="relative space-y-4">
                            <div>
                                <span className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Shift Status</span>
                                <h3 className="font-heading text-3xl font-bold mt-1 text-slate-800 dark:text-slate-100">
                                    {punchedOut ? "Punched Out" : punchedIn ? "Active Shift" : "Not Checked In"}
                                </h3>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {punchedIn && <span>Started: {new Date(today.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                                {punchedOut && <span> · Ended: {new Date(today.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                                {today?.duration_minutes ? <span> · Working: {fmtMin(today.duration_minutes)}</span> : null}
                            </p>
                        </div>
                        <div className="relative flex flex-col gap-3 pt-6">
                            <button
                                onClick={() => punch("in")}
                                disabled={!!punchedIn || acting}
                                className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-primary text-white font-semibold hover:bg-primary/95 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-400 transition"
                            >
                                <Play className="w-4.5 h-4.5" />
                                <span>Punch In</span>
                            </button>
                            <button
                                onClick={() => punch("out")}
                                disabled={!punchedIn || !!punchedOut || acting}
                                className="w-full flex items-center justify-center gap-2 h-12 rounded-xl border border-black/10 dark:border-white/10 text-foreground font-semibold hover:bg-black/5 dark:hover:bg-white/5 disabled:text-slate-400 disabled:border-slate-100 dark:disabled:border-slate-800 transition"
                            >
                                <Square className="w-4.5 h-4.5" />
                                <span>Punch Out</span>
                            </button>
                            <Link 
                                to="/attendance"
                                className="w-full flex items-center justify-center h-10 rounded-xl text-xs font-semibold text-primary hover:underline transition mt-2"
                            >
                                Detailed Attendance Tab
                            </Link>
                        </div>
                    </GlassCard>

                    {/* Recent History */}
                    <GlassCard className="p-6 lg:col-span-2 flex flex-col justify-between">
                        <div>
                            <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                Recent Shifts
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse text-sm">
                                    <thead>
                                        <tr className="border-b border-black/5 dark:border-white/5 text-slate-400 font-semibold text-xs">
                                            <th className="py-2.5">Date</th>
                                            <th className="py-2.5">Punch In</th>
                                            <th className="py-2.5">Punch Out</th>
                                            <th className="py-2.5">Work Hours</th>
                                            <th className="py-2.5">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.slice(0, 4).map((h, i) => (
                                            <tr key={i} className="border-b border-black/5 dark:border-white/5 last:border-0">
                                                <td className="py-3 font-medium text-slate-800 dark:text-slate-200">
                                                    {new Date(h.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                                                </td>
                                                <td className="py-3 text-slate-500">
                                                    {h.punch_in ? new Date(h.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—"}
                                                </td>
                                                <td className="py-3 text-slate-500">
                                                    {h.punch_out ? new Date(h.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—"}
                                                </td>
                                                <td className="py-3 text-slate-500">
                                                    {fmtMin(h.duration_minutes)}
                                                </td>
                                                <td className="py-3">
                                                    {h.is_late ? (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200/50">Late</span>
                                                    ) : (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200/50">On Time</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {history.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="py-6 text-center text-slate-400 font-semibold text-sm">
                                                    No shifts logged in the last 30 days.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </GlassCard>
                </div>

                {/* Bottom Row: Upcoming Meetings & Leaves Overview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Upcoming Meetings */}
                    <GlassCard className="p-6">
                        <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100 mb-6 flex items-center gap-2">
                            <Video className="w-5 h-5 text-primary" />
                            Upcoming Meetings
                        </h3>
                        <div className="h-44 flex flex-col justify-center items-center">
                            {meetings.length === 0 ? (
                                <span className="text-sm font-semibold text-slate-400">No scheduled meetings.</span>
                            ) : (
                                <div className="w-full space-y-3 overflow-y-auto max-h-44 pr-2">
                                    {meetings.slice(0, 3).map((m) => (
                                        <div key={m.id} className="flex justify-between items-center p-3 rounded-xl border border-black/5 dark:border-white/5">
                                            <div>
                                                <span className="text-sm font-semibold">{m.title}</span>
                                                <p className="text-xs text-slate-500">
                                                    {new Date(m.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {m.duration_minutes} mins
                                                </p>
                                            </div>
                                            <a 
                                                href={m.link || "#"} 
                                                target="_blank" 
                                                rel="noreferrer" 
                                                className="text-xs font-bold text-primary hover:underline"
                                            >
                                                Join call
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </GlassCard>

                    {/* Leaves Overview */}
                    <GlassCard className="p-6 flex flex-col justify-between">
                        <div>
                            <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100 mb-6 flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                Leaves Balance Overview
                            </h3>
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div className="p-3 rounded-xl border border-black/5 dark:border-white/5">
                                    <div className="text-xs text-slate-400 font-bold uppercase">Paid</div>
                                    <div className="text-2xl font-bold font-heading mt-1 text-slate-800 dark:text-slate-200">
                                        {leaveStats ? leaveStats.balance.paid - leaveStats.used.paid : 18}
                                    </div>
                                </div>
                                <div className="p-3 rounded-xl border border-black/5 dark:border-white/5">
                                    <div className="text-xs text-slate-400 font-bold uppercase">Sick</div>
                                    <div className="text-2xl font-bold font-heading mt-1 text-slate-800 dark:text-slate-200">
                                        {leaveStats ? leaveStats.balance.sick - leaveStats.used.sick : 12}
                                    </div>
                                </div>
                                <div className="p-3 rounded-xl border border-black/5 dark:border-white/5">
                                    <div className="text-xs text-slate-400 font-bold uppercase">Casual</div>
                                    <div className="text-2xl font-bold font-heading mt-1 text-slate-800 dark:text-slate-200">
                                        {leaveStats ? leaveStats.balance.casual - leaveStats.used.casual : 6}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="pt-6">
                            <Link 
                                to="/leaves" 
                                className="w-full flex items-center justify-center h-11 rounded-xl bg-primary/10 text-primary font-semibold hover:bg-primary/20 transition text-sm"
                            >
                                Apply Leave
                            </Link>
                        </div>
                    </GlassCard>
                </div>
            </div>
        );
    }

    // Admin / HR / Team Lead Dashboard UI
    return (
        <div className="space-y-8" data-testid="dashboard-view">
            {/* Header with welcome and Add Employee Action */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-slate-800 dark:text-slate-100">
                        Welcome back, {user?.name?.split(" ")[0] || "Gauransh"}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Here's what's happening across your organization today.
                    </p>
                </div>
                <button
                    onClick={() => navigate("/employees")}
                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/95 transition shadow-sm self-start sm:self-auto"
                >
                    <Plus className="w-4.5 h-4.5" />
                    <span>Add employee</span>
                </button>
            </div>

            {/* Metric Cards Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {/* Headcount */}
                <GlassCard 
                    onClick={() => navigate("/employees")}
                    className="p-5 flex flex-col justify-between h-36 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                >
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Total headcount</span>
                        <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                            {stats ? stats.total_employees : <Skeleton className="h-8 w-12" />}
                        </div>
                    </div>
                    <span className="text-xs font-semibold text-emerald-500 flex items-center gap-1">
                        +4 this year
                    </span>
                </GlassCard>

                {/* Alumni / Completed */}
                <GlassCard 
                    onClick={() => navigate("/alumni")}
                    className="p-5 flex flex-col justify-between h-36 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                >
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Alumni / Completed</span>
                        <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                            {stats ? stats.archived_count : 0}
                        </div>
                    </div>
                    <span className="text-xs font-semibold text-teal-500">
                        Completed internships
                    </span>
                </GlassCard>

                {/* On Leave Today */}
                <GlassCard 
                    onClick={() => navigate("/leaves")}
                    className="p-5 flex flex-col justify-between h-36 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                >
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">On leave today</span>
                        <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                            {stats ? stats.on_leave_today : 0}
                        </div>
                    </div>
                    <span className="text-xs font-semibold text-slate-400">
                        Approved leaves
                    </span>
                </GlassCard>

                {/* Present Today */}
                <GlassCard 
                    onClick={() => navigate("/attendance")}
                    className="p-5 flex flex-col justify-between h-36 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                >
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Present today</span>
                        <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                            {stats ? stats.attendance_today : 0}
                        </div>
                    </div>
                    <span className="text-xs font-semibold text-emerald-500">
                        Checked in today
                    </span>
                </GlassCard>

                {/* Active Projects */}
                <GlassCard 
                    onClick={() => navigate("/projects")}
                    className="p-5 flex flex-col justify-between h-36 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                >
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Active projects</span>
                        <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100">
                            {stats ? stats.active_projects : 3}
                        </div>
                    </div>
                    <span className="text-xs font-semibold text-emerald-500">
                        Across all teams
                    </span>
                </GlassCard>
            </div>

            {/* Middle Row: Pending Leave Requests & Upcoming Meetings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pending Leave Requests */}
                <GlassCard className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Pending leave requests</h3>
                        <Link to="/leaves" className="text-xs font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition">
                            View all
                        </Link>
                    </div>
                    
                    <div className="h-44 flex flex-col justify-center items-center">
                        {pendingLeaves.length === 0 ? (
                            <span className="text-sm font-semibold text-slate-400">No pending requests.</span>
                        ) : (
                            <div className="w-full space-y-3 overflow-y-auto max-h-44 pr-2">
                                {pendingLeaves.map((leave) => (
                                    <div key={leave.id} className="flex justify-between items-center p-3 rounded-xl border border-black/5 dark:border-white/5">
                                        <div>
                                            <span className="text-sm font-semibold">{leave.user_name}</span>
                                            <p className="text-xs text-slate-500">{leave.leave_type} Leave · {leave.start_date} to {leave.end_date}</p>
                                        </div>
                                        <Link to="/leaves" className="text-xs font-bold text-primary hover:underline">
                                            Manage
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </GlassCard>

                {/* Upcoming Meetings */}
                <GlassCard className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Upcoming meetings</h3>
                    </div>
                    
                    <div className="h-44 flex flex-col justify-center items-center">
                        {meetings.length === 0 ? (
                            <span className="text-sm font-semibold text-slate-400">Nothing scheduled.</span>
                        ) : (
                            <div className="w-full space-y-3 overflow-y-auto max-h-44 pr-2">
                                {meetings.slice(0, 3).map((m) => (
                                    <div key={m.id} className="flex justify-between items-center p-3 rounded-xl border border-black/5 dark:border-white/5">
                                        <div>
                                            <span className="text-sm font-semibold">{m.title}</span>
                                            <p className="text-xs text-slate-500">
                                                {new Date(m.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {m.duration_minutes} mins
                                            </p>
                                        </div>
                                        <Clock className="w-4 h-4 text-slate-400" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </GlassCard>
            </div>

            {/* Bottom Row: Headcount by department & Recent hires */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Headcount by department */}
                <GlassCard className="p-6">
                    <h3 className="font-heading font-bold text-lg mb-6 text-slate-800 dark:text-slate-100">Headcount by department</h3>
                    
                    <div className="space-y-5">
                        {deptDist.map((d, i) => (
                            <div key={i} className="space-y-1.5">
                                <div className="flex justify-between text-xs font-semibold">
                                    <span className="text-slate-700 dark:text-slate-300">{d.department}</span>
                                    <span className="text-slate-400">{d.count}</span>
                                </div>
                                <div className="w-full bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden">
                                    <div 
                                        className="bg-primary h-2.5 rounded-full transition-all duration-700" 
                                        style={{ width: `${(d.count / maxDeptCount) * 100}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </GlassCard>

                {/* Recent Hires */}
                <GlassCard className="p-6">
                    <h3 className="font-heading font-bold text-lg mb-6 text-slate-800 dark:text-slate-100">Recent hires</h3>
                    
                    <div className="space-y-4">
                        {hiresList.map((h, i) => {
                            const initials = h.name.split(" ").map(n => n[0]).join("").toUpperCase();
                            return (
                                <div key={i} className="flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        {h.avatar ? (
                                            <img src={h.avatar} alt={h.name} className="w-9 h-9 rounded-full object-cover border shadow-sm" />
                                        ) : (
                                            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary grid place-items-center font-bold text-xs">
                                                {initials}
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{h.name}</span>
                                            <p className="text-[11px] text-slate-500 font-medium">
                                                {h.role} · {h.dept}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="text-[11px] font-semibold text-slate-400 shrink-0">
                                        Joined {h.date}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </GlassCard>
            </div>

            {/* Online Visitors & Activity Logs - Admin/HR only */}
            {!isEmployeeOrIntern && stats?.active_logs && (
                <div className="mt-8 max-w-4xl space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* Online Employees Card */}
                        <GlassCard className="p-5 flex items-center justify-between border border-teal-150/50 dark:border-teal-900/50 bg-teal-50/5 dark:bg-teal-950/5">
                            <div>
                                <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Online Employees</span>
                                <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                    {stats.online_employees}
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-emerald-500">Currently Active</span>
                        </GlassCard>

                        {/* Online Interns Card */}
                        <GlassCard className="p-5 flex items-center justify-between border border-teal-150/50 dark:border-teal-900/50 bg-teal-50/5 dark:bg-teal-950/5">
                            <div>
                                <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Online Interns</span>
                                <div className="text-3xl font-bold font-heading mt-2 text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
                                    {stats.online_interns}
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-teal-500">Currently Active</span>
                        </GlassCard>
                    </div>

                    {/* Active User Logs Table */}
                    <GlassCard className="p-6 border border-slate-150 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40">
                        <h3 className="font-heading font-bold text-base mb-4 text-slate-800 dark:text-slate-100 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            Live Active Logs (IP & User Details)
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs text-left">
                                <thead className="text-[10px] text-muted-foreground uppercase border-b border-slate-100 dark:border-slate-800">
                                    <tr>
                                        <th className="py-2.5">Name</th>
                                        <th className="py-2.5">Email</th>
                                        <th className="py-2.5">Role</th>
                                        <th className="py-2.5">IP Address</th>
                                        <th className="py-2.5">Last Active</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 font-medium">
                                    {stats.active_logs.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="py-4 text-center text-muted-foreground">No active sessions logged in the last minute.</td>
                                        </tr>
                                    ) : (
                                        stats.active_logs.map((log) => (
                                            <tr key={log.user_id} className="text-slate-700 dark:text-slate-350">
                                                <td className="py-2.5 text-slate-900 dark:text-slate-100">{log.name}</td>
                                                <td className="py-2.5">{log.email}</td>
                                                <td className="py-2.5 uppercase text-[10px] tracking-wide font-bold">{log.role}</td>
                                                <td className="py-2.5 text-mono text-teal-600 dark:text-teal-400">{log.ip_address}</td>
                                                <td className="py-2.5 text-muted-foreground">{new Date(log.last_seen).toLocaleTimeString()}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </GlassCard>
                </div>
            )}
        </div>
    );
}
